/**
 * Google Identity Services sign-in.
 *
 * Ports the in-page GIS credential flow from legacy `Auth.tsx` onto the
 * monorepo's existing auth infrastructure:
 *
 *   GIS button  →  onCredential  →  useLoginWithGoogle (TanStack mutation)
 *                                     →  AuthApi.loginWithGoogle
 *                                         →  POST /api/auth/google
 *                                →  tokens.setSession
 *                                →  invalidate auth.me
 *                                →  navigate('/home')
 *
 * The parallel redirect-based `/auth/callback?credential=...` path (see
 * `OAuthCallback.tsx`) stays in place for a future redirect-mode switch.
 *
 * T19 Stage 3: lifted from the placeholder aesthetic to a card-based layout
 * using the existing `Card` primitive + the new `Wordmark` brand mark. All
 * styling now flows from tokens (CP5) — no more inline rems / hardcoded
 * inline shadows. Behaviour preserved: invite banner, error param banner,
 * config-issue alert, post-login `inviteError` alert + Continue button
 * (T16 Stage 4) all still render with the same `data-testid`s.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginWithEmail, useLoginWithGoogle } from '@shopista/shared/api/hooks';
import { apiErrorMessage, ApiError } from '@shopista/shared/api';
import type { GoogleAuthOutcome, GoogleAuthResponse } from '@shopista/shared/types';
import { Button, Card, Input, Logo } from '../components';
import { useSession, type WebTokenStorage } from '../auth';
import { routes } from './paths';
import { LoginVersionFooter } from './LoginVersionFooter';
import { GoogleConsentStep } from '../features/auth/GoogleConsentStep';
import { isNativePlatform, signInWithGoogleNative } from './nativeGoogleSignIn';
import { isAndroidBrowser } from '../lib/platform';
import { redirectToApp } from '../lib/appIntent';

const GOOGLE_CLIENT_ID_PATTERN = /^[0-9]+-[a-z0-9-]+\.apps\.googleusercontent\.com$/i;

// Basic well-formed-email check. Mirrors the backend `loginSchema` intent
// (z.string().email()) closely enough to catch obvious typos client-side and
// skip a doomed request to the server — not a full RFC validator.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';

function getGoogleClientIdIssue(value: string): string | null {
  if (!value) return 'Missing VITE_GOOGLE_CLIENT_ID.';
  if (value.includes('GOCSPX')) return 'Looks like a client secret, not a client ID.';
  if (!GOOGLE_CLIENT_ID_PATTERN.test(value)) {
    return 'Expected format: 12345-abc.apps.googleusercontent.com';
  }
  return null;
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: { credential?: string }) => void;
}
interface GoogleIdButtonOptions {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  width?: number;
}
interface GoogleAccountsId {
  initialize: (config: GoogleIdConfig) => void;
  renderButton: (parent: HTMLElement, options: GoogleIdButtonOptions) => void;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

const GIS_SCRIPT_ID = 'google-identity';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

// ---------------------------------------------------------------------------
// Layout styles — token-only.
//
// The page splits into:
//   pageStyle      → the outer <main>; centers the card vertically + provides
//                    safe-area padding on small viewports.
//   stackStyle     → vertical stack of card sections (wordmark, card, footer).
//   cardBodyStyle  → flow inside the Card; gap-driven so Card padding handles
//                    the outer rhythm.
//   alertStyle     → shared shape for the role=status / role=alert callouts;
//                    variants override the colors via `accent`.
// ---------------------------------------------------------------------------

// Exported for LoginPage.test.tsx — happy-dom's CSSOM collapses the
// `padding` shorthand + the `paddingTop` longhand override (it keeps only the
// shorthand value on the rendered node), so the safe-area-inset assertion reads
// this source object directly (same pattern FirstRunSetup.tsx uses for its
// header inset, T27.2.5.1).
export const pageStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-6) var(--space-4)',
  // T27.2.5.4 (A2 login half) — clear the device status bar on an edge-to-edge
  // Android WebView. `max(...)` keeps the normal top padding on web/desktop
  // (where the inset is 0) and grows it by the safe-area inset on a native
  // device, so the brand mark never falls under the clock/battery. The
  // safe-area plugin (T27.2.2) populates the real inset value. This longhand
  // overrides only the top of the `padding` shorthand above.
  paddingTop: 'max(var(--space-6), env(safe-area-inset-top, 0px))',
  // T29.1 (AC #5) — clear the device gesture-nav bar at the BOTTOM. The login
  // footer (LoginVersionFooter) is the last element in the centered stack; on a
  // gesture-nav Android device the nav bar overlaps the bottom edge, crowding
  // it. `max(...)` keeps the normal bottom padding on web/desktop (inset 0) and
  // grows it by `env(safe-area-inset-bottom)` on a native device. Same longhand
  // pattern as paddingTop; overrides only the bottom of the `padding` shorthand.
  paddingBottom: 'max(var(--space-6), env(safe-area-inset-bottom, 0px))',
  // T19 Stage 7 (added 2026-04-30): port the legacy `auth-container`
  // backdrop. The legacy spec layered three animated stripe-waves +
  // radial-gradient glows; we ship the static diagonal split for parity
  // (animation polish is out of T19 scope per the task spec). The card
  // itself stays white + shadowed to keep form text legible against the
  // gradient. T27.2.5.4 (G1): `--gradient-brand-backdrop` now follows the
  // theme — a calm dark wash under `[data-theme="dark"]` — so a dark-mode user
  // gets a login that matches the dark app instead of a bright lilac flash.
  background: 'var(--gradient-brand-backdrop)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
};

const stackStyle: CSSProperties = {
  width: '100%',
  maxWidth: '24rem', // comfortable single-column form width on phone + desktop
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 'var(--space-4)',
};

const wordmarkRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  // Tight gap between wordmark and card so they read as one unit.
  marginBottom: 'var(--space-2)',
};

const cardBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const headingStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — page title renders the Poppins display face.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-semibold)',
  textAlign: 'center',
};

const subheadingStyle: CSSProperties = {
  margin: 0,
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
};

const inviteBannerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-muted)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3) var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  margin: 0,
};

const errorBannerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-muted)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-status-danger)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3) var(--space-4)',
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
  margin: 0,
};

const inlineNoteStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const inlineErrorStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-status-danger)',
  margin: 0,
};

const mutedFooterStyle: CSSProperties = {
  margin: 0,
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

// T27.2.10.2 — public legal links below the access line. Reachable from inside
// the app (the login screen is the first surface a new / signed-out user sees),
// pointing at the public /privacy + /terms routes (outside AuthGuard).
const legalLinksRowStyle: CSSProperties = {
  margin: 0,
  textAlign: 'center',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const legalLinkStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  textDecoration: 'underline',
};

const buttonRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  // GIS renders a fixed-width button (set via the renderButton width option)
  // — centering happens at the row level so it sits flush in the card.
  alignItems: 'center',
  // Ensure the GIS iframe button does not collide with surrounding text.
  minHeight: '2.5rem',
};

// T27.2.5.4 (G2) — Google-branded native sign-in button. Follows Google's
// "Sign in with Google" branding (the four-color "G" mark + the label) while
// keeping every COLOR/spacing/radius/font value on app tokens (CP5): the only
// Google brand color lives in the static `/icons/google-g.svg` asset, which
// token-scan does not inspect. The button surface is the app's raised surface
// (white in light, dark slate in dark), so it reads correctly in both themes
// without hardcoding Google's button hex.
const googleButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  minHeight: '2.75rem', // ≥44px touch target (frontend-standards §3)
  padding: 'var(--space-2) var(--space-4)',
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  borderWidth: 'var(--border-width-hairline)',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-lg)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
};

const googleGlyphStyle: CSSProperties = {
  // The 4-color Google "G" mark — fixed brand glyph, square, vertically
  // centered with the label. Sized off the type scale (1.25rem ≈ the label cap
  // height) so it never needs a raw px.
  width: '1.25rem',
  height: '1.25rem',
  display: 'block',
  flexShrink: 0,
};

// T27.2.10.1 — email + password form. Token-only (CP5): the field stack,
// the "or" divider with centered label, and the submit button all read from
// the same design tokens the rest of the card uses.
const emailFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const orDividerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
};

const orLineStyle: CSSProperties = {
  flex: 1,
  height: '0.0625rem',
  backgroundColor: 'var(--color-border-subtle)',
};

const inviteErrorTitleStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  marginBottom: 'var(--space-1)',
};

const inviteErrorBodyStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
};

export interface LoginPageProps {
  tokens: WebTokenStorage;
}

export function LoginPage({ tokens }: LoginPageProps): JSX.Element | null {
  // Guest-only guard. The login route stays in the SPA back-history, so when
  // an already-authenticated native user presses the Android hardware Back
  // button and `history.back()` walks onto `/login`, we must not re-render the
  // sign-in UI — the session is still valid (tokens live in the Keystore), the
  // user only *looks* logged out. Mirror AuthGuard's session pattern: redirect
  // an authenticated session straight to home, and never flash the login UI
  // while the session is still resolving (`hydrating`). The actual sign-in flow
  // (`unauthenticated` branch) is unchanged. The early returns live AFTER every
  // hook below so the rules-of-hooks order stays stable across renders.
  const session = useSession({ tokens });

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement | null>(null);

  const [googleReady, setGoogleReady] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID || '').trim();
  const inviteToken = params.get('invite');
  const errorParam = params.get('error');
  const clientIdIssue = getGoogleClientIdIssue(GOOGLE_CLIENT_ID);

  const loginMutation = useLoginWithGoogle();
  const emailLoginMutation = useLoginWithEmail();

  // T27.2.10.1 — email + password form state. The form sits beside Google and
  // feeds the SAME success applier (apply session, surface inviteError, navigate)
  // through the email-login mutation.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // T16 Stage 4 AC 4.3/4.4: when sign-in succeeds but the invite was rejected
  // (mismatched email, expired, hard-deleted) the backend returns a successful
  // `{ sessionToken, user }` response with `inviteError: '...'` riding along.
  // We must surface that string instead of dropping it on the floor — the
  // user thinks they joined the inviting household but didn't.
  const [postLoginInviteError, setPostLoginInviteError] = useState<string | null>(
    null,
  );

  // T27.2.2.1: native (Capacitor) shell can't run the web GIS flow inside the
  // WebView, so it uses the OS account picker. The branch only affects how the
  // Google ID token is obtained; the success path below is shared.
  const native = isNativePlatform();
  const [nativeError, setNativeError] = useState<string | null>(null);
  const [nativePending, setNativePending] = useState(false);

  // T49.3 — the Google "consent required" step. A NEW Google user reaching
  // `/google` without consent gets `{ consentRequired: true }`; we surface the
  // friendly "Almost there" step instead of dead-ending, and stash the Google
  // credential so "Finish signing up" can re-call `/google` with consent.
  const [consentRequired, setConsentRequired] = useState(false);
  const pendingCredentialRef = useRef<string | null>(null);

  // Shared success applier — Google (web GIS + native picker) AND the email +
  // password form all funnel their response through this: apply the session,
  // persist the native refresh token, surface inviteError, navigate. Do not
  // fork this (T27.2.2.1 / T27.2.10.1).
  const applyAuthSuccess = useCallback(
    (resp: GoogleAuthResponse): void => {
      tokens.setSession({
        accessToken: resp.sessionToken,
        user: resp.user,
      });
      // T27.2.2.2 — persist the native-only refresh token into the Keystore so
      // the next /refresh sends it. `setRefreshToken` is defined only on the
      // native secure provider; on web it is undefined (cookie path).
      if (resp.refreshToken && tokens.setRefreshToken) {
        tokens.setRefreshToken(resp.refreshToken);
      }
      if (inviteToken) {
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        window.history.replaceState({}, '', `${url.pathname}${url.search}`);
      }
      // T16 Stage 4 AC 4.3/4.4: surface inviteError without blocking sign-in.
      if (resp.inviteError) {
        setPostLoginInviteError(resp.inviteError);
        return;
      }
      navigate(routes.home, { replace: true });
    },
    // navigate + tokens are stable identities; inviteToken is the only reactive
    // input. The exhaustive-deps rule is not registered in this project.
    [inviteToken],
  );

  // T49.3 — branch the `/google` outcome. A returning user gets the session body
  // (apply it + navigate). A NEW user without consent gets `{ consentRequired }`
  // — surface the "Almost there" step rather than treating it as a success.
  const handleGoogleOutcome = useCallback(
    (result: GoogleAuthOutcome): void => {
      if ('consentRequired' in result) {
        setConsentRequired(true);
        return;
      }
      applyAuthSuccess(result);
    },
    [applyAuthSuccess],
  );

  // Shared Google success handler — both the web GIS callback and the native
  // picker feed the *same* credential into the *same* mutation. The credential is
  // stashed so the consent step can re-call `/google` with `termsAccepted: true`.
  const onCredential = useCallback(
    (credential: string, termsAccepted?: boolean): void => {
      pendingCredentialRef.current = credential;
      // T27.2.2.2 — on native, flag the login so the backend returns the refresh
      // token in the body (the WebView cookie drops cross-site). Web omits it.
      const base = native ? { credential, native: true } : { credential };
      const withInvite = inviteToken ? { ...base, inviteToken } : base;
      // T49.3 — carry consent on the re-call from the "Almost there" step.
      const payload = termsAccepted ? { ...withInvite, termsAccepted: true } : withInvite;

      loginMutation.mutate(payload, { onSuccess: handleGoogleOutcome });
    },
    [inviteToken, native, handleGoogleOutcome],
  );

  // T49.3 — "Finish signing up": re-run the Google exchange WITH consent using
  // the stashed credential. The same Google ID token is still valid for the
  // immediate re-call; the backend then provisions + records the acceptance.
  const onConsentFinish = useCallback(() => {
    const credential = pendingCredentialRef.current;
    if (credential) onCredential(credential, true);
  }, [onCredential]);

  const onConsentSwitchAccount = useCallback(() => {
    pendingCredentialRef.current = null;
    setConsentRequired(false);
  }, []);

  // T27.2.10.1 — email + password submit. Generic error surface (the backend
  // returns a generic "invalid email or password" — never reveals which).
  const onEmailSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      setEmailError(null);
      // Catch an obvious email typo before submitting — the backend rejects a
      // malformed address with a 400 whose body is a Zod-error array, which
      // apiErrorMessage can't read, so it would leak the raw HTTP message.
      // Trim first: mobile keyboards append a trailing space after an email
      // autofill, which would fail the pattern on an otherwise-valid address.
      const normalizedEmail = email.trim();
      if (!EMAIL_PATTERN.test(normalizedEmail)) {
        setEmailError(INVALID_EMAIL_MESSAGE);
        return;
      }
      const base = native
        ? { email: normalizedEmail, password, native: true }
        : { email: normalizedEmail, password };
      const payload = inviteToken ? { ...base, inviteToken } : base;
      emailLoginMutation.mutate(payload, {
        onSuccess: applyAuthSuccess,
        onError: (err) => {
          // A 400 is loginSchema validation (e.g. malformed email): show the
          // email-format message. The body is a Zod-error array that
          // apiErrorMessage can't surface, so never route it through there.
          if (err instanceof ApiError && err.status === 400) {
            setEmailError(INVALID_EMAIL_MESSAGE);
            return;
          }
          // 401 and everything else: generic, never reveals which field.
          setEmailError(apiErrorMessage(err, 'Invalid email or password.'));
        },
      });
    },
    [email, password, native, inviteToken, emailLoginMutation, applyAuthSuccess],
  );

  // T27.2.2.1: native sign-in button handler — open the OS account picker,
  // obtain the Google ID token, then run the shared success path.
  const handleNativeSignIn = useCallback(async (): Promise<void> => {
    setNativeError(null);
    setNativePending(true);
    try {
      const idToken = await signInWithGoogleNative(GOOGLE_CLIENT_ID);
      onCredential(idToken);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Google sign-in failed.';
      setNativeError(message);
    } finally {
      setNativePending(false);
    }
  }, [GOOGLE_CLIENT_ID, onCredential]);

  useEffect(() => {
    // Native shell uses the OS account picker (handleNativeSignIn), not GIS.
    if (native) return;
    if (clientIdIssue) return;

    let cancelled = false;

    const initializeGoogle = (): void => {
      if (cancelled) return;
      const accountsId = window.google?.accounts?.id;
      if (!accountsId || !buttonRef.current) return;

      accountsId.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          if (!response?.credential) return;
          // Shared success path (T27.2.2.1) — identical to the native branch.
          onCredential(response.credential);
        },
      });

      accountsId.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
      });

      setGoogleReady(true);
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return () => {
        cancelled = true;
      };
    }

    const existing = document.getElementById(GIS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', initializeGoogle);
      return () => {
        cancelled = true;
        existing.removeEventListener('load', initializeGoogle);
      };
    }

    const script = document.createElement('script');
    script.id = GIS_SCRIPT_ID;
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;
    script.onerror = () => {
      if (!cancelled) setScriptError('Failed to load Google sign-in.');
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
    // Deps intentionally exclude `loginMutation`, `navigate`, and `tokens`.
    // `loginMutation` (TanStack `useMutation` result) returns a fresh object
    // every render; including it re-fires the effect on every state setter
    // (e.g. `setPostLoginInviteError` below) and calls
    // `accountsId.initialize()` multiple times, which triggers Google's
    // `[GSI_LOGGER]: ... is called multiple times` warning. `navigate`
    // (react-router-dom) and `tokens` (prop) are stable identities in normal
    // use. Same pattern as InviteConsumer.tsx (lines 169–173). `onCredential`
    // is memoised on `[inviteToken]`, so it is covered by the dep below.
  }, [GOOGLE_CLIENT_ID, clientIdIssue, inviteToken, native, onCredential]);

  const loginErrorMessage =
    loginMutation.error instanceof Error
      ? loginMutation.error.message
      : loginMutation.error
        ? 'Login failed.'
        : null;

  // Guest-only redirect / hold (see the useSession note at the top). Placed
  // after every hook so the hook order is identical on every render.
  if (session.status === 'authenticated') {
    return <Navigate to={routes.home} replace />;
  }
  if (session.status === 'hydrating') {
    // Don't flash the sign-in card while `/auth/me` is in flight — a logged-in
    // user pressing Back would otherwise see a flicker of the login screen.
    return (
      <main style={pageStyle} data-testid="login-page">
        <div style={stackStyle}>
          <p data-testid="login-hydrating" style={inlineNoteStyle}>
            Loading…
          </p>
        </div>
      </main>
    );
  }

  // T27.2.11 Part B — mobile sign-up funnel. A GUEST on a mobile Android browser
  // is routed INTO the app (open the app if installed, else Play). Placed AFTER
  // the authenticated/hydrating guards above, so an authenticated user still
  // redirects home (never to the intent); it reaches here only when the session
  // is `unauthenticated`. Gated on `isAndroidBrowser()` so desktop / iOS /
  // inside the native app all fall through to the form.
  //
  // The form still renders below (no `return null`): an `intent://` navigation
  // can be refused — DevTools mobile emulation has no handler for the scheme,
  // and Android Chrome blocks the launch without a user gesture — and a refused
  // launch on a blank page dead-ends sign-in entirely. Where the launch
  // succeeds the app covers the page, so the brief form render costs nothing.
  if (isAndroidBrowser()) {
    redirectToApp();
  }

  // T49.3 — the Google "Almost there" consent step replaces the sign-in card when
  // a new Google user reached `/google` without consent. The same stack/wordmark
  // chrome is kept so it reads as one continuous screen.
  if (consentRequired) {
    return (
      <main style={pageStyle} data-testid="login-page">
        <div style={stackStyle}>
          <div style={wordmarkRowStyle}>
            <Logo size={80} showText data-testid="login-logo" />
          </div>
          <GoogleConsentStep
            pending={loginMutation.isPending}
            onFinish={onConsentFinish}
            onUseDifferentAccount={onConsentSwitchAccount}
          />
        </div>
      </main>
    );
  }

  return (
    <main style={pageStyle} data-testid="login-page">
      <div style={stackStyle}>
        <div style={wordmarkRowStyle}>
          {/*
            T19 Stage 6/7/8 (added 2026-04-30): full brand mark above the
            card — gradient SVG icon + gradient wordmark. size=80 mirrors the
            legacy `auth-card` hero scale. No link here (Open Decision #5:
            wordmark navigates to /home only inside the AppShell header).
          */}
          <Logo size={80} showText data-testid="login-logo" />
        </div>

        <Card padding="lg" shadow="md" data-testid="login-card">
          <div style={cardBodyStyle}>
            <h1 style={headingStyle}>Sign in</h1>
            <p style={subheadingStyle}>
              Keep your household list, stores, and products in sync.
            </p>

            {inviteToken && (
              <p
                role="status"
                data-testid="login-invite"
                style={inviteBannerStyle}
              >
                Invite detected. Sign in to join your household.
              </p>
            )}

            {errorParam && (
              <p
                role="alert"
                data-testid="login-error"
                style={inlineErrorStyle}
              >
                {errorParam}
              </p>
            )}

            {postLoginInviteError && (
              <div
                role="alert"
                data-testid="login-invite-error"
                style={errorBannerStyle}
              >
                <div style={inviteErrorTitleStyle}>
                  Invite could not be applied
                </div>
                <div style={inviteErrorBodyStyle}>{postLoginInviteError}</div>
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(routes.home, { replace: true })}
                    data-testid="login-invite-continue"
                  >
                    Continue to app
                  </Button>
                </div>
              </div>
            )}

            {clientIdIssue && (
              <div
                role="alert"
                data-testid="login-config-issue"
                style={errorBannerStyle}
              >
                <div style={inviteErrorTitleStyle}>
                  Google sign-in is not configured
                </div>
                <div style={inviteErrorBodyStyle}>
                  Set <code>VITE_GOOGLE_CLIENT_ID</code> in{' '}
                  <code>apps/web/.env.local</code> (or <code>.env</code>) and
                  restart the dev server.
                </div>
                <div
                  style={{
                    ...inviteErrorBodyStyle,
                    marginTop: 'var(--space-1)',
                  }}
                >
                  {clientIdIssue}
                </div>
              </div>
            )}

            {/* T27.2.10.1 — email + password sign-in. Renders for everyone
                (web + native), independent of the Google client-id config, so a
                provisioned reviewer can sign in without Google. */}
            <form
              style={emailFormStyle}
              onSubmit={onEmailSubmit}
              data-testid="login-email-form"
              noValidate
            >
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email-input"
                required
              />
              <Input
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                reveal
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                data-testid="login-password-input"
                required
              />
              <Button
                type="submit"
                variant="primary"
                disabled={emailLoginMutation.isPending}
                data-testid="login-email-submit"
              >
                {emailLoginMutation.isPending ? 'Signing in…' : 'Sign in'}
              </Button>
              {emailError && (
                <p
                  role="alert"
                  data-testid="login-email-error"
                  style={inlineErrorStyle}
                >
                  {emailError}
                </p>
              )}
              {/* T27.2.10.1 Phase B — forgot-password entry. Routes to the
                  non-enumerating request-reset screen. */}
              <p style={inlineNoteStyle}>
                <Link
                  to={routes.resetRequest}
                  style={legalLinkStyle}
                  data-testid="login-forgot-password"
                >
                  Forgot your password?
                </Link>
              </p>
            </form>

            {!clientIdIssue && (
              <>
                <div style={orDividerRowStyle} aria-hidden="true">
                  <span style={orLineStyle} />
                  <span>or</span>
                  <span style={orLineStyle} />
                </div>
                {native ? (
                  // T27.2.2.1: native shell — opens the OS Google account
                  // picker (web GIS can't run in a WebView), feeding the shared
                  // onCredential success path. T27.2.5.4 (G2): styled as a
                  // Google-branded "Sign in with Google" button (the four-color
                  // "G" mark from the static asset + the label) instead of a
                  // plain purple <Button>, since this is the first thing a new
                  // Android user sees. The handler (signInWithGoogleNative) is
                  // unchanged — only the button presentation is.
                  <>
                    <div style={buttonRowStyle}>
                      <button
                        type="button"
                        style={googleButtonStyle}
                        onClick={() => {
                          void handleNativeSignIn();
                        }}
                        disabled={nativePending || loginMutation.isPending}
                        data-testid="google-signin-native"
                      >
                        <img
                          src="/icons/google-g.svg"
                          alt=""
                          aria-hidden="true"
                          style={googleGlyphStyle}
                          data-testid="google-signin-glyph"
                        />
                        <span>Sign in with Google</span>
                      </button>
                    </div>
                    {nativePending && (
                      <p style={inlineNoteStyle}>Opening Google…</p>
                    )}
                    {nativeError && (
                      <p
                        role="alert"
                        data-testid="login-native-error"
                        style={inlineErrorStyle}
                      >
                        {nativeError}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div style={buttonRowStyle}>
                      <div
                        ref={buttonRef}
                        data-testid="google-signin-button"
                        style={{ minHeight: '2.5rem' }}
                      />
                    </div>
                    {!googleReady && !scriptError && (
                      <p style={inlineNoteStyle}>Loading Google sign-in…</p>
                    )}
                    {scriptError && (
                      <p role="alert" style={inlineErrorStyle}>
                        {scriptError}
                      </p>
                    )}
                  </>
                )}
                {loginMutation.isPending && (
                  <p data-testid="login-pending" style={inlineNoteStyle}>
                    Signing in…
                  </p>
                )}
                {loginErrorMessage && (
                  <p
                    role="alert"
                    data-testid="login-mutation-error"
                    style={inlineErrorStyle}
                  >
                    {loginErrorMessage}
                  </p>
                )}
              </>
            )}
          </div>
        </Card>

        {/* T27.2.10.1 Phase B — public self-serve sign-up entry. New users no
            longer need an invite; the link routes to the Create account screen
            (email + password + bot challenge). */}
        <p style={mutedFooterStyle} data-testid="login-create-account">
          New to Shopista?{' '}
          <Link
            to={routes.register}
            style={legalLinkStyle}
            data-testid="login-create-account-link"
          >
            Create an account
          </Link>
        </p>

        {/* T27.2.10.2 — public legal links. The login screen is the first surface
            a signed-out user sees, so the Privacy Policy + Terms are reachable
            from here (they also live in App settings once signed in). Both routes
            are public (outside AuthGuard). */}
        <p style={legalLinksRowStyle} data-testid="login-legal-links">
          <Link to={routes.privacy} style={legalLinkStyle} data-testid="login-privacy-link">
            Privacy Policy
          </Link>
          {' · '}
          <Link to={routes.terms} style={legalLinkStyle} data-testid="login-terms-link">
            Terms of Service
          </Link>
        </p>

        {/* T29 — pre-auth version footer: both app + server compact by default,
            5-tap / long-press reveals full detail. No login required. */}
        <LoginVersionFooter />
      </div>
    </main>
  );
}
